import { MAP, NAV_LAYERS } from '../config.js';

/**
 * 段差を上り下りできる歩行マップ（メッシュのスキャン用）。
 *
 * navGrid.js は「床は1枚」という前提なので、階段や一段高い浴室の床は家具扱いになって入れない。
 * こちらはマスごとに「立てる面」を複数持ち（1階の床・階段の各段・中二階・ロフト）、
 * 隣のマスとの高さの差が段差の上限以内なら移動できる、という規則でつなぐ。
 *
 * - 立てる面   … 上を向いた面の点が集まっている高さ
 * - 頭上       … 面のすぐ上に物（テーブルの天板・ロフトの床裏）があれば立てない
 * - 側面       … 面より一段上〜体の高さに縦の面（壁・家具の側面）があれば立てない
 * - 体の幅     … 上下左右のマスにも近い高さの面があるときだけ立てる
 * - 到達可能   … 開始地点の床から、段差の上限以内で塗りつぶして届く面だけ
 *
 * 面の向きが分かるメッシュでしか使えない。作れなければ null を返す。
 *
 * @param {object} source sceneSource（forEachSurfacePoint の4番目の引数が上向き成分 -1〜1）
 */
export function buildLayeredNav(source, { links = [] } = {}) {
  if (source?.kind !== 'model' || typeof source.forEachSurfacePoint !== 'function') return null;

  const P = NAV_LAYERS;
  const origin = source.navOrigin;
  const R = source.navRadius;
  const CELL = P.cell;
  const N = Math.round((R * 2) / CELL);
  const ox = origin.x;
  const oz = origin.z;

  const indexOf = (x, z) => {
    const gx = Math.floor((x - ox + R) / CELL);
    const gz = Math.floor((z - oz + R) / CELL);
    if (gx < 0 || gz < 0 || gx >= N || gz >= N) return -1;
    return gz * N + gx;
  };

  // マスごとに点（高さ, 上向き成分）を振り分ける
  const buckets = Array.from({ length: N * N }, () => []);
  // 点の色。buckets[k] の i 番目の点（y, up の組）の色が colors[k][i / 2]。見取りの色付けにだけ使う
  const colors = Array.from({ length: N * N }, () => []);
  source.forEachSurfacePoint((x, y, z, up, rgb) => {
    const k = indexOf(x, z);
    if (k < 0 || typeof up !== 'number') return;
    buckets[k].push(y, up);
    colors[k].push(rgb ?? DEFAULT_RGB);
  });

  const layers = findStandableSurfaces(buckets, P);
  const fitted = keepBodyWidth(layers, N, P.step);

  const start = findStart(fitted, N, indexOf(ox, oz), P);
  if (start === null) return null;

  const canBridge = createBridgeCheck(buckets, P);
  const flood = createFlood(fitted, N, P, canBridge);
  flood.spreadFrom(start.k, start.height);
  applyLinks(links, flood, fitted, N, indexOf, P);
  const { reachable } = flood;
  let area = 0;
  for (const heights of reachable) if (heights.length > 0) area += 1;
  area *= CELL * CELL;
  if (area < P.minAreaM2) return null;

  const floorY = start.height;
  const tallSide = markTallSides(buckets, floorLevels(reachable), P);
  const ceilings = collectCeilings(buckets, P);
  const layout = buildLayout(buckets, colors, reachable, N, P);

  const nearestIn = (k, preferY) => {
    const heights = reachable[k];
    if (heights.length === 0) return null;
    return heights.reduce((best, h) => (Math.abs(h - preferY) < Math.abs(best - preferY) ? h : best));
  };

  return Object.freeze({
    floorY,
    area,
    /**
     * シンプル表示用の見取り（simpleMap.js が使う）。
     * 座標はマス番号 (gx, gz)。ワールド座標は x = (gx + 0.5) * cell + minX。
     */
    layout: Object.freeze({ ...layout, size: N, cell: CELL, minX: ox - R, minZ: oz - R }),
    /**
     * (x, z) に、今いる高さ fromY から上り下りできる面があればその高さを返す。無ければ null。
     */
    heightAt(x, z, fromY) {
      const k = indexOf(x, z);
      if (k < 0) return null;
      const h = nearestIn(k, fromY);
      return h !== null && Math.abs(h - fromY) <= P.step ? h : null;
    },
    isWalkable(x, z) {
      const k = indexOf(x, z);
      return k >= 0 && reachable[k].length > 0;
    },
    /**
     * (x, z) の、高さ fromY から見た頭上の天井（ロフトの床裏など）の高さ。無ければ null。
     * ソファの背もたれ程度の低い物は天井とみなさない。
     */
    ceilingAt(x, z, fromY) {
      const k = indexOf(x, z);
      if (k < 0) return null;
      for (const h of ceilings[k]) if (h > fromY + P.ceilingMinClearance) return h;
      return null;
    },
    isCameraOpen(x, z) {
      const k = indexOf(x, z);
      return k >= 0 && !tallSide[k] && (reachable[k].length > 0 || fitted[k].length > 0);
    },
    /** 指定地点に一番近い、歩ける面（preferY に近い高さ）を返す */
    nearestWalkable(x, z, preferY = floorY) {
      const k0 = indexOf(x, z);
      const cx = k0 < 0 ? start.k % N : k0 % N;
      const cz = k0 < 0 ? (start.k / N) | 0 : (k0 / N) | 0;
      for (let r = 0; r < N; r += 1) {
        let best = null;
        for (let dz = -r; dz <= r; dz += 1) {
          for (let dx = -r; dx <= r; dx += 1) {
            if (Math.max(Math.abs(dx), Math.abs(dz)) !== r) continue;
            const gx = cx + dx;
            const gz = cz + dz;
            if (gx < 0 || gz < 0 || gx >= N || gz >= N) continue;
            const h = nearestIn(gz * N + gx, preferY);
            if (h === null) continue;
            const score = Math.hypot(dx, dz) + Math.abs(h - preferY);
            if (best === null || score < best.score) best = { gx, gz, h, score };
          }
        }
        if (best !== null) {
          return {
            x: (best.gx + 0.5) * CELL - R + ox,
            z: (best.gz + 0.5) * CELL - R + oz,
            y: best.h,
          };
        }
      }
      return null;
    },
  });
}

/** マスごとに、上向きの面が集まっている高さを列挙し、頭上と側面が空いているものだけ残す */
function findStandableSurfaces(buckets, P) {
  return buckets.map((points) => {
    const bins = new Map();
    for (let i = 0; i < points.length; i += 2) {
      if (points[i + 1] <= P.upFacing) continue;
      const bin = Math.round(points[i] / P.bin);
      bins.set(bin, (bins.get(bin) ?? 0) + 1);
    }

    // 隣り合う刻みはひとつの面にまとめ、点数で重み付けした高さを使う
    const surfaces = [];
    for (const bin of [...bins.keys()].sort((a, b) => a - b)) {
      const count = bins.get(bin);
      if (count < P.minSurfacePoints) continue;
      const last = surfaces[surfaces.length - 1];
      if (last && bin - last.topBin <= 1) {
        last.sum += bin * count;
        last.count += count;
        last.topBin = bin;
      } else {
        surfaces.push({ sum: bin * count, count, topBin: bin });
      }
    }

    return surfaces
      .map((s) => (s.sum / s.count) * P.bin)
      .filter((h) => {
        let overhead = 0;
        let side = 0;
        for (let i = 0; i < points.length; i += 2) {
          const dy = points[i] - h;
          const horizontal = Math.abs(points[i + 1]) > P.upFacing;
          if (horizontal && dy > P.overheadMin && dy < P.headroom) overhead += 1;
          // 一段ぶん上までの縦の面は、次の段の蹴上げなので数えない
          if (!horizontal && dy > P.step + P.sideMargin && dy < P.sideMax) side += 1;
        }
        return overhead < P.maxOverheadPoints && side < P.maxSidePoints;
      });
  });
}

/** 上下左右のマスにも近い高さの面があるものだけ残す（壁際や細い隙間にめり込まないため） */
function keepBodyWidth(layers, N, step) {
  return layers.map((heights, k) => {
    const x = k % N;
    const z = (k / N) | 0;
    return heights.filter((h) => {
      for (const [dx, dz] of NEIGHBORS) {
        const gx = x + dx;
        const gz = z + dz;
        if (gx < 0 || gz < 0 || gx >= N || gz >= N) return false;
        if (!layers[gz * N + gx].some((h2) => Math.abs(h2 - h) <= step)) return false;
      }
      return true;
    });
  });
}

/** 開始地点の近くで、床として一番自然な（最も低い）面を探す */
function findStart(fitted, N, k0, P) {
  if (k0 < 0) return null;
  const cx = k0 % N;
  const cz = (k0 / N) | 0;
  for (let r = 0; r < P.startSearchCells; r += 1) {
    for (let dz = -r; dz <= r; dz += 1) {
      for (let dx = -r; dx <= r; dx += 1) {
        if (Math.max(Math.abs(dx), Math.abs(dz)) !== r) continue;
        const gx = cx + dx;
        const gz = cz + dz;
        if (gx < 0 || gz < 0 || gx >= N || gz >= N) continue;
        const k = gz * N + gx;
        if (fitted[k].length === 0) continue;
        return { k, height: Math.min(...fitted[k]) };
      }
    }
  }
  return null;
}

/**
 * 開始地点から、段差の上限以内の移動だけで届く面を塗りつぶす。
 *
 * 加えて「敷居越え」を許す。浴室の入口の敷居やドアレールは、実物は数cmでも
 * メッシュでは 30〜50cm の盛り上がりに膨らんで記録され、段差の上限を超えてしまう。
 * 幅が数マス以内の盛り上がりで、向こう側に同じ高さの床があり、間に壁が無ければ、
 * 盛り上がりを今の高さの床とみなして通れるようにする。
 */
function createFlood(fitted, N, P, canBridge) {
  const reachable = Array.from({ length: fitted.length }, () => []);
  const visited = new Set();

  const add = (k, h) => {
    const key = `${k}:${h}`;
    if (visited.has(key)) return false;
    visited.add(key);
    reachable[k].push(h);
    return true;
  };

  const spread = (stack) => {
    while (stack.length > 0) {
      const [k, h] = stack.pop();
      const x = k % N;
      const z = (k / N) | 0;
      for (const [dx, dz] of NEIGHBORS) {
        const inside = (s) => {
          const gx = x + dx * s;
          const gz = z + dz * s;
          return gx >= 0 && gz >= 0 && gx < N && gz < N ? gz * N + gx : -1;
        };

        const kk = inside(1);
        if (kk < 0) continue;
        let stepped = false;
        for (const h2 of fitted[kk]) {
          if (Math.abs(h2 - h) > P.step) continue;
          stepped = true;
          if (add(kk, h2)) stack.push([kk, h2]);
        }
        if (stepped) continue;

        // 隣に立てる面が無いときだけ、細い盛り上がりの向こう側を探す
        for (let span = 2; span <= P.bridgeCells + 1; span += 1) {
          const target = inside(span);
          if (target < 0) break;
          const landing = fitted[target].find((h2) => Math.abs(h2 - h) <= P.step);
          if (landing === undefined) continue;
          const between = [];
          for (let s = 1; s < span; s += 1) between.push(inside(s));
          if (!between.every((mid) => canBridge(mid, h))) break;
          for (const mid of between) add(mid, h);
          if (add(target, landing)) stack.push([target, landing]);
          break;
        }
      }
    }
  };

  /** (startK, startH) を到達済みにして、そこから広げる。通路の端のように既に登録済みでも広げ直す */
  const spreadFrom = (startK, startH) => {
    add(startK, startH);
    spread([[startK, startH]]);
  };

  return { reachable, add, spreadFrom };
}

/**
 * 物件データで指定した通路（walkLinks）をつなぐ。
 * スキャンが崩れて自動判定では通れない所（床メッシュの抜けた浴室の入口、開いたままのドア等）を、
 * 人が「ここは通れる」と指定して補う。片方の端に到達できていれば、幅3マスの通路を敷いて
 * もう片方の端から塗りつぶしを続ける。通路が別の通路の先にあってもよいよう、変化が無くなるまで繰り返す。
 */
function applyLinks(links, flood, fitted, N, indexOf, P) {
  const pending = links.filter((l) => Array.isArray(l?.from) && Array.isArray(l?.to));
  let progressed = true;
  while (progressed && pending.length > 0) {
    progressed = false;
    for (let i = pending.length - 1; i >= 0; i -= 1) {
      const { from, to } = pending[i];
      const a = indexOf(from[0], from[1]);
      const b = indexOf(to[0], to[1]);
      if (a < 0 || b < 0) {
        pending.splice(i, 1);
        continue;
      }
      // 到達済みの端から、まだの端へ向かって敷く
      const [src, dst, srcXZ, dstXZ] =
        flood.reachable[a].length > 0 ? [a, b, from, to] : flood.reachable[b].length > 0 ? [b, a, to, from] : [];
      if (src === undefined) continue;

      // 高さが指定されていればそれに近い面を使う（中二階の下の床と上の床を取り違えないため）
      const nearest = (list, target) => list.reduce((best, v) => (Math.abs(v - target) < Math.abs(best - target) ? v : best));
      const h = nearest(flood.reachable[src], srcXZ[2] ?? flood.reachable[src][0]);
      const wantDst = dstXZ[2] ?? h;
      const h2 = fitted[dst].length > 0 ? nearest(fitted[dst], wantDst) : wantDst;

      const length = Math.hypot(dstXZ[0] - srcXZ[0], dstXZ[1] - srcXZ[1]);
      const steps = Math.max(1, Math.ceil(length / (P.cell / 2)));
      for (let s = 0; s <= steps; s += 1) {
        const t = s / steps;
        const x = srcXZ[0] + (dstXZ[0] - srcXZ[0]) * t;
        const z = srcXZ[1] + (dstXZ[1] - srcXZ[1]) * t;
        const k = indexOf(x, z);
        if (k < 0) continue;
        const y = h + (h2 - h) * t;
        // キャラの体が引っかからないよう、進行方向に対して上下左右1マスも通路にする
        for (const [dx, dz] of [[0, 0], ...NEIGHBORS]) {
          const gx = (k % N) + dx;
          const gz = ((k / N) | 0) + dz;
          if (gx >= 0 && gz >= 0 && gx < N && gz < N) flood.add(gz * N + gx, y);
        }
      }
      flood.spreadFrom(dst, h2);
      pending.splice(i, 1);
      progressed = true;
    }
  }
}

/**
 * 高さ h の床から、そのマスを敷居として通過してよいか。
 * - 盛り上がりが低い（上向きの面がすべて h + bridgeMaxRise 以下）
 * - 腰〜頭の高さに縦の面が無い（壁やドアそのものではない）
 */
function createBridgeCheck(buckets, P) {
  return (k, h) => {
    const points = buckets[k];
    let wall = 0;
    for (let i = 0; i < points.length; i += 2) {
      const dy = points[i] - h;
      const horizontal = Math.abs(points[i + 1]) > P.upFacing;
      if (horizontal && points[i + 1] > 0 && dy > P.bridgeMaxRise && dy < P.bridgeWallMax) return false;
      if (!horizontal && dy > P.bridgeWallMin && dy < P.bridgeWallMax) wall += 1;
    }
    return wall < P.bridgeWallPoints;
  };
}

/**
 * 背の高い縦の面（壁）があるマス。カメラを壁の向こうに回り込ませないために使う。
 * 2階建ての家では、どの階の床から見ても壁になる面を探す（levels = 歩ける床の高さの一覧）。
 */
function markTallSides(buckets, levels, P) {
  return buckets.map((points) => {
    for (const floorY of levels) {
      let count = 0;
      for (let i = 0; i < points.length; i += 2) {
        const dy = points[i] - floorY;
        if (Math.abs(points[i + 1]) <= P.upFacing && dy > P.wallMin && dy < P.wallMax) count += 1;
      }
      if (count > P.wallPoints) return 1;
    }
    return 0;
  });
}

/** 歩ける面の高さを 0.5m 刻みにまとめた一覧（階段の途中も含む） */
function floorLevels(reachable) {
  const set = new Set();
  for (const heights of reachable) for (const h of heights) set.add(Math.round(h * 2) / 2);
  return [...set].sort((a, b) => a - b);
}

/** マスごとに、十分な広さのある水平面の高さを低い順に並べる（天井の判定用） */
function collectCeilings(buckets, P) {
  return buckets.map((points) => {
    const bins = new Map();
    for (let i = 0; i < points.length; i += 2) {
      if (Math.abs(points[i + 1]) <= P.upFacing) continue;
      const bin = Math.round(points[i] / P.bin);
      bins.set(bin, (bins.get(bin) ?? 0) + 1);
    }
    return [...bins.entries()]
      .filter(([, count]) => count >= P.ceilingPoints)
      .map(([bin]) => bin * P.bin)
      .sort((a, b) => a - b);
  });
}

/**
 * スキャンの点から、きれいな箱で描ける見取りを作る。
 * - 床   … 歩ける面。階段の段は下まで埋めて段に見せる
 * - 壁   … 腰〜頭の高さに縦の面がたくさんあるマス
 * - 家具 … 歩けない、床から少し上の水平面（テーブル・ソファ・浴槽など）
 * 歩ける場所から離れたマス（部屋の外のゴミ）は描かない。
 */
function buildLayout(buckets, colors, reachable, N, P) {
  // 近くの歩ける床の一番低い高さ。壁や家具の足元になる
  const nearFloor = new Float32Array(N * N).fill(NaN);
  const r = MAP.nearCells;
  for (let gz = 0; gz < N; gz += 1) {
    for (let gx = 0; gx < N; gx += 1) {
      let low = Infinity;
      for (let dz = -r; dz <= r; dz += 1) {
        for (let dx = -r; dx <= r; dx += 1) {
          const x = gx + dx;
          const z = gz + dz;
          if (x < 0 || z < 0 || x >= N || z >= N) continue;
          for (const h of reachable[z * N + x]) low = Math.min(low, h);
        }
      }
      if (low !== Infinity) nearFloor[gz * N + gx] = low;
    }
  }

  const floors = [];
  const walls = [];
  const furniture = [];
  for (let k = 0; k < N * N; k += 1) {
    const base = nearFloor[k];
    if (Number.isNaN(base)) continue;
    const gx = k % N;
    const gz = (k / N) | 0;
    const points = buckets[k];
    /** 条件に合う点の平均色 */
    const colorOf = (test) => averageColor(points, colors[k], test);

    for (const h of reachable[k]) {
      // 下の床から近い高さなら段として下まで埋める。ロフトのように高い床は板にする
      const rise = h - base;
      const bottom = rise > 0.05 && rise <= MAP.solidStepMax && reachable[k].length === 1 ? base : h - MAP.slab;
      floors.push(gx, gz, bottom, h, colorOf((y, up) => up > P.upFacing && Math.abs(y - h) <= MAP.colorBand));
    }

    const verticals = [];
    let upper = 0;
    for (let i = 0; i < points.length; i += 2) {
      if (Math.abs(points[i + 1]) > P.upFacing) continue;
      const dy = points[i] - base;
      if (dy > P.wallMin && dy < P.wallMax) upper += 1;
      if (dy > 0) verticals.push(points[i]);
    }
    if (upper > P.wallPoints && !reachable[k].some((h) => Math.abs(h - base) <= P.step)) {
      verticals.sort((a, b) => a - b);
      const top = verticals[Math.floor((verticals.length - 1) * MAP.wallTopQuantile)];
      const wallTop = Math.min(top, base + MAP.wallMaxHeight);
      walls.push(gx, gz, base, wallTop, colorOf((y, up) => Math.abs(up) <= P.upFacing && y > base + 0.2 && y < wallTop));
      continue;
    }

    if (reachable[k].length > 0) continue;
    const bins = new Map();
    for (let i = 0; i < points.length; i += 2) {
      if (points[i + 1] <= P.upFacing) continue;
      const dy = points[i] - base;
      if (dy < MAP.furnitureMin || dy > MAP.furnitureMax) continue;
      const bin = Math.round(points[i] / P.bin);
      bins.set(bin, (bins.get(bin) ?? 0) + 1);
    }
    let top = -Infinity;
    for (const [bin, count] of bins) if (count >= MAP.furniturePoints) top = Math.max(top, bin * P.bin);
    if (top !== -Infinity) {
      furniture.push(gx, gz, base, top, colorOf((y, up) => up > P.upFacing && Math.abs(y - top) <= MAP.colorBand));
    }
  }

  return { floors, walls, furniture, baseY: Math.min(...floors.filter((_, i) => i % LAYOUT_STRIDE === 3)) };
}

/** 見取りの並びの1件あたりの要素数: gx, gz, bottom, top, 色 */
export const LAYOUT_STRIDE = 5;
const DEFAULT_RGB = 0xbbbbbb;

/** test(y, up) を満たす点の平均色。該当が無ければ全点の平均 */
function averageColor(points, rgbs, test) {
  let r = 0;
  let g = 0;
  let b = 0;
  let n = 0;
  for (let pass = 0; pass < 2 && n === 0; pass += 1) {
    for (let i = 0; i < points.length; i += 2) {
      if (pass === 0 && !test(points[i], points[i + 1])) continue;
      const c = rgbs[i / 2];
      r += (c >> 16) & 255;
      g += (c >> 8) & 255;
      b += c & 255;
      n += 1;
    }
  }
  if (n === 0) return DEFAULT_RGB;
  return (Math.round(r / n) << 16) | (Math.round(g / n) << 8) | Math.round(b / n);
}

const NEIGHBORS = [[1, 0], [-1, 0], [0, 1], [0, -1]];
